import type { CSSProperties } from 'react';
import { useState, useEffect, useMemo } from 'react';
import PageHeader from '@/components/PageHeader';
import { useMetaTags } from '@/hooks/useMetaTags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { CodeXml, Loader2, Pencil, Check, ExternalLink, Upload, Palette, FileText, Settings, Share2, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useSuperadminAuth } from '@/contexts/AdminAuthContext';
import { useTheme } from '@/contexts/ThemeProvider';
// Using Netlify function upload-to-cdn instead of Cloudinary
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import * as Sentry from "@sentry/react";
import { useTranslation } from '@/hooks/useTranslation';
import { mergeSettingsCache } from '@/hooks/useSettings';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/i18n/translations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getNetlifyFunctionsRoot } from '@/lib/utils';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';
interface Setting {
  key: string;
  label?: string | null;
  value: string;
  read_only?: boolean;
  updated_at?: string;
  album_id: string;
}

interface AlbumInfo {
  id: string;
  slug: string;
  is_active: boolean;
}

const Config = () => {
  const { albumId } = useAlbumContext();
  const { isSuperadmin } = useSuperadminAuth();
  const { tokens } = useTheme();
  const { toast } = useToast();
  const { getAlbumPath } = useAlbumNavigation();
  const { t } = useTranslation();


  useMetaTags({
    title: t('adminConfigMetaTitle'),
    pathname: '/admin/config'
  });

  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSettingKey, setEditingSettingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [themeNames, setThemeNames] = useState<{ [key: string]: string }>({});
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File }>({});
  const [uploadingSettings, setUploadingSettings] = useState<Set<string>>(new Set());
  const [albumInfo, setAlbumInfo] = useState<AlbumInfo | null>(null);
  const [savedLang, setSavedLang] = useState<string>(DEFAULT_LANGUAGE);
  const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANGUAGE);
  const [savingLang, setSavingLang] = useState<boolean>(false);
  const cardSurface = tokens.colors.card || tokens.colors.background || '0 0% 100%';
  const cardText = tokens.colors['card-foreground'] || tokens.colors.foreground;
  const cardSurfaceStyle = useMemo<CSSProperties>(
    () => toSurfaceContrastVariables(getSurfaceContrastColors(cardSurface, cardText)) as CSSProperties,
    [cardSurface, cardText],
  );

  const updateSetting = async (key: string, newValue: string, albumId: string) => {
    try {
      // console.log("Updating", key, "to", newValue, "in album", albumId);

      const { data, error } = await supabase
        .from('settings')
        .update({ value: newValue })
        .eq('key', key)
        .eq('album_id', albumId)
        .select();

      if (error) {
        console.error('Error updating setting:', error);
        toast({
          title: t('error'),
          description: t('settingsUpdateErrorDescription'),
          variant: "destructive"
        });
        throw error;
      }

      toast({
        title: t('settingUpdatedTitle'),
        description: t('settingUpdatedDescription', { key })
      });

      setSettings(prev =>
        prev.map(setting =>
          setting.key === key && setting.album_id === albumId
            ? { ...setting, value: newValue }
            : setting
        )
      );
      mergeSettingsCache(albumId, { [key]: newValue });
    } catch (error) {
      Sentry.captureMessage(`Error updating setting: ${error instanceof Error ? error.message : String(error)} `, "error");
      console.error('Error updating setting:', error);
    }
  };

  const handleToggleChange = async (setting: Setting) => {
    if (setting.read_only) return;
    const newValue = setting.value === 'true' ? 'false' : 'true';
    await updateSetting(setting.key, newValue, setting.album_id);
  };

  const handleEditSave = async (key: string) => {
    await updateSetting(key, editingValue, albumId);
    setEditingSettingKey(null);
    setEditingValue('');
  };

  const handleImageUpload = async (setting: Setting, file: File) => {
    try {
      // Enforce max size 1 MB for direct-to-Bunny uploads
      const MAX_BYTES = 1 * 1024 * 1024;
      if (file.size > MAX_BYTES) {
        toast({
          title: t('error'),
          description: 'El archivo supera el tamaño máximo de 1 MB',
          variant: 'destructive',
        });
        return;
      }

      // Add to uploading set
      setUploadingSettings(prev => new Set(prev).add(setting.key));

      // Determine filename with extension
      const mapTypeToExt = (type: string) => {
        if (!type) return '';
        if (type.includes('jpeg')) return '.jpg';
        if (type.includes('jpg')) return '.jpg';
        if (type.includes('png')) return '.png';
        if (type.includes('webp')) return '.webp';
        if (type.includes('gif')) return '.gif';
        return '';
      };
      const existingName = file.name || '';
      const hasExt = /\.[a-zA-Z0-9]+$/.test(existingName);
      const ext = hasExt ? '' : mapTypeToExt(file.type) || '.jpg';
      const sanitized = existingName.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = (sanitized || `${setting.key}_${Date.now()}`) + (hasExt ? '' : ext);

      // Call Netlify function to upload to Bunny storage
      const res = await fetch(`${getNetlifyFunctionsRoot()}/upload-to-cdn?fileName=${encodeURIComponent(fileName)}&albumId=${encodeURIComponent(albumId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: file,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.body || data?.error || 'Error al subir la imagen al CDN');
      }

      // Save the CDN public URL to the database
      await updateSetting(setting.key, data.url, albumId);

      toast({
        title: t('imageUploadedTitle'),
        description: t('imageUploadedDescription')
      });
    } catch (error) {
      Sentry.captureMessage(`Upload error: ${error instanceof Error ? error.message : String(error)}`, "error");
      console.error('Upload error:', error);
      toast({
        title: t('error'),
        description: t('imageUploadFailed'),
        variant: "destructive"
      });
    } finally {
      // Remove from uploading set
      setUploadingSettings(prev => {
        const newSet = new Set(prev);
        newSet.delete(setting.key);
        return newSet;
      });
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('album_id', albumId)
        .order('label');

      if (error) throw error;
      setSettings(data || []);

      // Initialize language selection from fetched settings
      const langSettingRow = (data || []).find((s) => s.key === 'lang');
      const initialLang = langSettingRow?.value || DEFAULT_LANGUAGE;
      setSavedLang(initialLang);
      setSelectedLang(initialLang);

      // Fetch theme names for theme_selection settings
      const themeSelectionSetting = data?.find(s => s.key === 'theme_selection');
      if (themeSelectionSetting) {
        try {
          const selection = JSON.parse(themeSelectionSetting.value);
          if (selection.type === 'preset' && selection.theme_id) {
            const { data: themeData } = await supabase
              .from('themes')
              .select('name')
              .eq('id', selection.theme_id)
              .single();

            if (themeData) {
              setThemeNames(prev => ({ ...prev, [selection.theme_id]: themeData.name }));
            }
          }
        } catch (e) {
          Sentry.captureMessage(`Error parsing theme selection: ${e instanceof Error ? e.message : String(e)}`, "error");
          console.error('Error parsing theme selection:', e);
        }
      }
    } catch (error) {
      Sentry.captureMessage(`Error fetching settings: ${error instanceof Error ? error.message : String(error)}`, "error");
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('albums')
        .select('id, slug, is_active')
        .eq('id', albumId)
        .single();

      if (error) throw error;
      setAlbumInfo(data);
    } catch (error) {
      Sentry.captureMessage(`Error fetching album info: ${error instanceof Error ? error.message : String(error)}`, "error");
      console.error('Error fetching album info:', error);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: t('copied'),
        description: t('idCopiedToClipboard')
      });
    } catch (error) {
      Sentry.captureMessage(`Error copying to clipboard: ${error instanceof Error ? error.message : String(error)}`, "error");
      console.error('Error copying to clipboard:', error);
      toast({
        title: t('error'),
        description: t('copyToClipboardFailed'),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (albumId) {
      fetchSettings();
      fetchAlbumInfo();
    }
  }, [albumId]);

  const handleLangSave = async () => {
    if (!albumId) return;
    setSavingLang(true);
    try {
      // Try to update existing setting first
      const { data: updated, error: updateError } = await supabase
        .from('settings')
        .update({ value: selectedLang })
        .eq('key', 'lang')
        .eq('album_id', albumId)
        .select();

      if (updateError) throw updateError;

      if (!updated || updated.length === 0) {
        // No existing row, insert new
        const { error: insertError } = await supabase
          .from('settings')
          .insert({
            album_id: albumId,
            key: 'lang',
            value: selectedLang,
            label: 'Idioma'
          });
        if (insertError) throw insertError;
      }

      // Update local state and list
      setSavedLang(selectedLang);
      setSettings((prev) => {
        const idx = prev.findIndex((s) => s.key === 'lang');
        if (idx >= 0) {
          const clone = [...prev];
          clone[idx] = { ...clone[idx], value: selectedLang };
          return clone;
        }
        return [...prev, { key: 'lang', value: selectedLang, album_id: albumId, label: 'Idioma' } as Setting];
      });

      toast({
        title: t('settingUpdatedTitle'),
        description: t('settingUpdatedDescription', { key: 'lang' })
      });

      // Force page refresh to apply new language across the app
      window.location.reload();
    } catch (error) {
      Sentry.captureMessage(`Error saving lang setting: ${error instanceof Error ? error.message : String(error)}`, 'error');
      console.error('Error saving lang setting:', error);
      toast({
        title: t('error'),
        description: t('settingsUpdateErrorDescription'),
        variant: 'destructive'
      });
    } finally {
      setSavingLang(false);
    }
  };

  // Categorize settings
  const themeSettings = settings.filter(s => s.key === 'theme_selection');
  const textSettings = settings.filter(s => s.key.startsWith('text_'));
  const imageSettings = settings.filter(s => s.key.startsWith('image_'));
  const shareSettings = settings.filter(s => s.key.startsWith('share_'));
  const otherSettings = settings.filter(s =>
    !s.key.startsWith('text_') &&
    !s.key.startsWith('image_') &&
    !s.key.startsWith('meta_') &&
    !s.key.startsWith('share_') &&
    s.key !== 'theme_selection' &&
    s.key !== 'lang'
  );
  const coverSetting = imageSettings.find((setting) => setting.key === 'image_cover');
  const panelDesignPath = albumId ? `/panel/eventos/${albumId}?tab=design` : '/panel/eventos/listado';
  const panelSharePath = albumId ? `/panel/eventos/${albumId}?tab=share` : '/panel/eventos/listado';
  const currentThemeLabel = (() => {
    const setting = themeSettings[0];
    if (!setting?.value) return 'No configurado';

    try {
      const selection = JSON.parse(setting.value);
      if (selection.type === 'custom') {
        return 'Tema personalizado';
      }
      if (selection.type === 'preset' && selection.theme_id) {
        return themeNames[selection.theme_id] || 'Tema predefinido';
      }
    } catch {
      return 'No configurado';
    }

    return 'No configurado';
  })();

  const renderSetting = (setting: Setting, showUpload = false) => {
    if (setting.key === 'theme_selection') {
      return (
        <div className="flex items-center justify-between p-4 border rounded-lg bg-background">
          <div className="flex-1">
            <p className="font-medium text-md">{setting.label || setting.key}</p>
            <p className="text-sm text-muted-foreground">
              {(() => {
                try {
                  const selection = JSON.parse(setting.value);
                  if (selection.type === 'custom') {
                    return 'Tema personalizado';
                  } else if (selection.type === 'preset' && selection.theme_id) {
                    return themeNames[selection.theme_id] || 'Tema predefinido';
                  }
                  return 'No configurado';
                } catch {
                  return 'No configurado';
                }
              })()}
            </p>
          </div>
          <Link to={getAlbumPath("/admin/theme")}>
            <Button size="sm" variant="outline">
              <ExternalLink className="w-4 h-4 mr-1" />
              Editar
            </Button>
          </Link>
        </div>
      );
    }

    if (showUpload) {
      const selectedFile = selectedFiles[setting.key];
      const isUploading = uploadingSettings.has(setting.key);

      return (
        <div className="space-y-4 p-4 border rounded-lg bg-background">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium text-md">{setting.label || setting.key}</p>
              {setting.read_only && (
                <span className="text-xs text-muted-foreground">(Solo lectura)</span>
              )}
            </div>
          </div>

          {/* Current saved image preview or upload spinner */}
          {isUploading ? (
            <div className="mb-4 flex items-center justify-center p-8 border rounded-lg bg-muted/50">
              <div className="text-center space-y-2">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Subiendo imagen...</p>
              </div>
            </div>
          ) : setting.value && !selectedFile ? (
            <div className="mb-4">
              <img
                src={setting.key === 'share_overlay' && setting.value.includes('cloudinary.com')
                  ? setting.value.replace('/upload/', '/upload/w_400/')
                  : setting.value
                }
                alt="Preview"
                className={setting.key === 'share_overlay' ? 'max-w-60 h-auto object-cover rounded border' : 'max-w-32 max-h-32 object-cover rounded border'}
              />
            </div>
          ) : null}

          {/* Selected file preview */}
          {selectedFile && !isUploading && (
            <div className="mb-4">
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Selected preview"
                className="max-w-32 max-h-32 object-cover rounded border"
              />
            </div>
          )}

          {/* File upload and upload button */}
          {!setting.read_only && !isUploading && (
            <div className="space-y-3">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedFiles(prev => ({ ...prev, [setting.key]: file }));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && selectedFile && !isUploading) {
                    handleImageUpload(setting, selectedFile);
                    setSelectedFiles(prev => {
                      const newFiles = { ...prev };
                      delete newFiles[setting.key];
                      return newFiles;
                    });
                  }
                }}
                className="flex-1"
              />
              {selectedFile && (
                <Button
                  onClick={() => {
                    handleImageUpload(setting, selectedFile);
                    setSelectedFiles(prev => {
                      const newFiles = { ...prev };
                      delete newFiles[setting.key];
                      return newFiles;
                    });
                  }}
                  className="w-full"
                  disabled={isUploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Subir
                </Button>
              )}
            </div>
          )}
        </div>
      );
    }

    if (setting.value === 'true' || setting.value === 'false') {
      return (
        <div className="flex items-center justify-between p-4 border rounded-lg  bg-background text-foreground">
          <div className="flex-1">
            <p className="font-medium text-md">{setting.label || setting.key}</p>
            {setting.read_only && (
              <span className="text-xs text-muted-foreground">(Solo lectura)</span>
            )}
          </div>
          <Switch
            checked={setting.value === 'true'}
            onCheckedChange={() => handleToggleChange(setting)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleToggleChange(setting);
              }
            }}
            disabled={setting.read_only}
          />
        </div>
      );
    }

    return (
      <div className="p-4 border rounded-lg  bg-background">
        <div className="flex items-center justify-between mb-2">
          <p className="font-medium text-md">{setting.label || setting.key}</p>
          {setting.read_only && (
            <span className="text-xs text-muted-foreground">(Solo lectura)</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {editingSettingKey === setting.key ? (
            <>
              <Input
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEditSave(setting.key);
                  }
                }}
                className="flex-1"
              />
              {!setting.read_only && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEditSave(setting.key)}
                >
                  <Check className="w-4 h-4" />
                </Button>
              )}
            </>
          ) : (
            <>
              <span className="flex-1 text-sm break-all">{setting.value}</span>
              {!setting.read_only && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingSettingKey(setting.key);
                    setEditingValue(setting.value);
                  }}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title={t('settingsTitle')} />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title={t('configAreaTitle')} />

      <div className="max-w-6xl mx-auto px-4 py-8">


        <CardHeader className='text-center mb-10'>
          <CardDescription className="mb-5">–––– 🔐 Privilegio y gestión de <strong className="text-sm">BloomKeep</strong> 💅🏻 ––––</CardDescription>
          <CardTitle >{t('albumCustomization')}</CardTitle>
        </CardHeader>

        {/* Album Info Widget - Only for Superadmins */}
        {isSuperadmin && albumInfo && (
          <Card className="mb-10" style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10">
              <div className="flex items-center space-x-2">
                <Settings className="w-5 h-5 text-primary-foreground" />
                <CardTitle className="text-primary-foreground">{t('albumInfoTitle')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="text-[hsl(var(--surface-fg))]">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Nombre Column */}
                <div className="space-y-1">
                  <div className="text-md text-[hsl(var(--surface-muted-fg))]">{t('nameLabel')}</div>
                  <div className="text-xl font-medium truncate text-[hsl(var(--surface-fg))]">{albumInfo.slug}</div>
                </div>

                {/* Estado Column */}
                <div className="space-y-1">
                  <div className="text-md text-[hsl(var(--surface-muted-fg))]">{t('statusLabel')}</div>
                  <div className={`font-medium ${albumInfo.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {albumInfo.is_active ? t('active') : t('inactive')}
                  </div>
                </div>

                {/* ID Column */}
                <div className="space-y-1">
                  <div className="text-md text-[hsl(var(--surface-muted-fg))]">{t('idLabel')}</div>
                  <div className="flex items-center space-x-2">
                    <code className="bg-muted px-2 py-1 rounded text-xs font-mono truncate cursor-pointer flex-1">
                      {albumInfo.id.substring(0, 8)}...
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(albumInfo.id)}
                      className="shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Language Column */}
                <div className="space-y-1">
                  <div className="text-md text-[hsl(var(--surface-muted-fg))]">{t('languageLabel')}</div>
                  <div className="flex items-center space-x-2 text-[hsl(var(--surface-fg))]">
                    <Select value={selectedLang} onValueChange={(val) => setSelectedLang(val)}>
                      <SelectTrigger
                        className="w-[160px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const expanded = (e.currentTarget as unknown as HTMLElement).getAttribute('aria-expanded');
                            if (expanded !== 'true' && selectedLang !== savedLang && !savingLang) {
                              e.preventDefault();
                              handleLangSave();
                            }
                          }
                        }}
                      >
                        <SelectValue placeholder={savedLang} />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedLang !== savedLang && (
                      <Button size="sm" onClick={handleLangSave} disabled={savingLang}>
                        {t('save')}
                      </Button>
                    )}
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


          {/* Imágenes */}
          <Card style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10 text-primary-foreground">
              <div className="flex items-center space-x-2  pt-4 leading-snug">
                <Palette className="w-5 h-5 " />
                <CardTitle>{t('designTitle')}</CardTitle>
              </div>
              <CardDescription className="text-inherit">{t('designDescription')}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-border bg-background p-5 space-y-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-foreground">La edición visual se movió al panel</p>
                    <p className="text-sm text-muted-foreground">
                      Portada, presets, colores semánticos y tipografías ahora se gestionan desde
                      {' '}
                      <strong>Event Details &gt; Diseño</strong>.
                    </p>
                  </div>
                  <Link to={panelDesignPath}>
                    <Button size="sm" variant="outline">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Abrir diseño en el panel
                    </Button>
                  </Link>
                </div>

                <div className="grid gap-4 md:grid-cols-[140px_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-xl border border-border bg-muted/40">
                    {coverSetting?.value ? (
                      <img
                        src={coverSetting.value}
                        alt="Portada actual"
                        className="h-full min-h-[120px] w-full object-cover"
                      />
                    ) : (
                      <div className="flex min-h-[120px] items-center justify-center text-sm text-muted-foreground">
                        Sin portada cargada
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Tema actual</div>
                      <div className="mt-1 text-base font-semibold text-foreground">{currentThemeLabel}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Portada</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {coverSetting?.value ? 'La portada actual se mantiene y ahora se edita desde el panel.' : 'Todavía no hay una portada configurada.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>



          {/* Textos */}
          <Card style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10 text-primary-foreground">
              <div className="flex items-center space-x-2  pt-4 leading-snug">
                <FileText className="w-5 h-5" />
                <CardTitle>{t('textsTitle')}</CardTitle>
              </div>
              <CardDescription className='text-inherit'>{t('textsDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {textSettings.length > 0 ? (
                textSettings.map((setting) => (
                  <div key={setting.key}>
                    {renderSetting(setting)}
                  </div>
                ))
              ) : (
                <p className="text-sm text-[hsl(var(--surface-muted-fg))]">{t('noTextSettingsAvailable')}</p>
              )}
            </CardContent>
          </Card>


          {/* Etiquetas Meta */}
          <Card style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10 text-primary-foreground">
              <div className="flex items-center space-x-2 pt-4 leading-snug">
                <CodeXml className="w-5 h-5 text-primary-foreground" />
                <CardTitle className="text-primary-foreground">{t('metaTitle')}</CardTitle>
              </div>
              <CardDescription className="text-inherit py-4">
                Los metadatos del enlace ahora se gestionan desde la pestaña Compartir en el panel del evento.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-indigo-100 bg-white/90 p-5 shadow-sm">
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Título, descripción e imagen OG ahora se editan desde Event Details para evitar duplicar esta configuración.
                  </p>
                  <div className="flex">
                    <Link to={panelSharePath}>
                      <Button size="sm" variant="outline">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Abrir Compartir en el Panel
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marco para compartir */}
          <Card style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10 text-primary-foreground">
              <div className="flex items-center space-x-2 pt-4 leading-snug">
                <Share2 className="w-5 h-5 text-primary-foreground" />
                <CardTitle >{t('shareFrameTitle')}</CardTitle>
              </div>
              <CardDescription className="text-inherit">{t('shareFrameDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-[hsl(var(--surface-fg))]">
              {shareSettings.length > 0 ? (
                shareSettings.map((setting) => (
                  <div key={setting.key}>
                    {renderSetting(setting, setting.key === 'share_overlay')}
                  </div>
                ))
              ) : (
                <p className="text-sm text-[hsl(var(--surface-muted-fg))]">{t('noShareSettingsAvailable')}</p>
              )}
            </CardContent>
          </Card>

          {/* Cositas */}
          <Card className='hidden' style={cardSurfaceStyle}>
            <CardHeader className="bg-primary rounded-t-lg mb-10 text-primary-foreground">
              <div className="flex items-center space-x-2 pt-4 leading-snug">
                <Settings className="w-5 h-5 text-primary-foreground" />
                <CardTitle>{t('miscTitle')}</CardTitle>
              </div>
              <CardDescription className="text-inherit">{t('generalSettingsDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-[hsl(var(--surface-fg))]">
              {otherSettings.length > 0 ? (
                otherSettings.map((setting) => (
                  <div key={setting.key}>
                    {renderSetting(setting)}
                  </div>
                ))
              ) : (
                <p className="text-sm text-[hsl(var(--surface-muted-fg))]">{t('noOtherSettingsAvailable')}</p>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Config;
